import { AxiosResponse, isAxiosError } from 'axios';
import { ref, Ref, computed } from 'vue';
import pMap from 'p-map';
import api from '@ilteducation/customer-mgmt/src/api';
import { CustomerMgmtTypes } from '@ilteducation/customer-mgmt';
import { SlimOrganization } from '@ilteducation/my-account-types';
import { checkQrCodeEditablePermission } from '@ilteducation/customer-mgmt-permissions';

import { Prettify } from '../../types';
import { Role } from '../../types/user';
import { isEmployeeOrAdmin } from '../../utils';
import useToast from '../../composables/useToast';
import { useLocale } from '../../composables/useLocale';
import { usePermissions } from '../../composables/usePermissions';
import { Duplicates, Exists, InvalidEmail, InvalidGroup, InvalidName } from '../Conflict/types';

export type Validations = Partial<
  Prettify<
    Record<Duplicates['type'], Duplicates> &
      Record<Exists['type'], Exists> &
      Record<InvalidName['type'], InvalidName> &
      Record<InvalidEmail['type'], InvalidEmail> &
      Record<InvalidGroup['type'], InvalidGroup>
  >
>;

export type UserToCreate = {
  key: string;
  uid?: string;
  userName?: string;
  active?: boolean;
  name: string;
  role: Role;
  email: string | undefined;
  password: string;
  qrToken?: string;
  status: 'pending' | 'success' | 'error' | 'processing' | 'checkingExistingUsers';
  errorMessages: string;
  existCheckHash?: string;
  editing?: boolean;
  validations?: Validations;
  groupUid?: string;
  groupName?: string;
};

export type CreatedUser = Omit<UserToCreate, 'uid' | 'userName'> & { uid: string; userName: string };

export function useCreateMultipleUsers() {
  const { t } = useLocale();
  const { toast } = useToast();
  const { permissionContext, getCallerAssociation, reporterBoolean } = usePermissions();

  const toErrorMessage = (error: unknown): string => {
    if (isAxiosError(error)) {
      const status = error.response?.status;
      if (status === 401) {
        return t('lcp.authorization_error');
      } else if (status === 403) {
        return t('lcp.forbidden_error');
      } else if (status === 409) {
        return t('lcp.conflict_error');
      } else if (status === 400) {
        return t('lcp.invalid_input');
      } else if (status === 422) {
        return t('lcp.validation_error');
      }
    }

    return t('lcp.failed_to_create_user');
  };

  const createdUsers: Ref<CreatedUser[]> = ref([]);
  const failedUsers: Ref<Record<string, UserToCreate>> = ref({});
  const isCreating = ref(false);
  const isUsersCreated = ref(false);
  const progress = ref(0);

  const createUsers = async (org: SlimOrganization, parsedUsers: Ref<UserToCreate[]>) => {
    if (!parsedUsers.value || parsedUsers.value.length === 0) {
      console.error('No users to create', parsedUsers);
      toast(t('lcp.no_users_to_create'), 'error');
      return;
    }
    if (isCreating.value) return;

    isCreating.value = true;
    progress.value = 0;

    const users = parsedUsers.value.filter((e) => e.status !== 'success');
    let index = 0;

    await pMap(
      users,
      async (user) => {
        await createUser(org, user);
        index++;
        progress.value = Math.round((index / users.length) * 100);
      },
      { concurrency: 10 },
    );

    isCreating.value = false;
    isUsersCreated.value = true;

    const hasErrors = usersWithError.value.length > 0;

    const toastMessage = hasErrors
      ? t('lcp.failed_to_create_users', { count: parsedUsers.value.filter((u) => u.status === 'error').length })
      : t('lcp.successfully_created_users', { count: createdUsers.value.length });
    toast(toastMessage, hasErrors ? 'error' : 'success');
  };

  const createUser = async (org: SlimOrganization, user: UserToCreate) => {
    user.status = 'processing';
    try {
      let userUpsertResponse: AxiosResponse<CustomerMgmtTypes.CreateUserResponse | void> | null = null;

      const qrEnabledForUserRole = await checkQrCodeEditablePermission(
        permissionContext.value,
        { organization: org, role: user.role },
        getCallerAssociation,
        reporterBoolean,
      );

      const qrToken: string | undefined = qrEnabledForUserRole ? user.qrToken : undefined;

      if (!user.uid) {
        const nameParts = user.name.trim().replace(/\s+/g, ' ').split(' ');
        const firstName = nameParts.length === 1 ? nameParts[0] : nameParts.slice(0, -1).join(' ').trim();
        const lastName = nameParts.length === 1 ? '' : nameParts.slice(-1).join('').trim();
        const memberships = user.groupUid ? { memberships: [{ groupUid: user.groupUid }] } : {};

        const createUserData: CustomerMgmtTypes.CreateUserRequest = {
          firstName,
          lastName,
          email: user.email?.trim() || undefined,
          password: user.password,
          qrToken,
          schoolAssociations: [{ schoolUid: org.uid.trim(), role: user.role, ...memberships }],
        };

        userUpsertResponse = await api.user.create(createUserData);
      } else {
        const updateUserData = {
          password: isEmployeeOrAdmin(user.role) ? undefined : user.password,
          qrToken,
          active: user.active === false ? true : undefined,
        };

        if (Object.values(updateUserData).some((v) => v !== undefined)) {
          userUpsertResponse = await api.user.patch(user.uid, updateUserData);
        }
      }

      if (userUpsertResponse === null) {
        // nothing updated
        if (user.uid && user.userName) {
          // TS fix, else should never happen
          createdUsers.value.push({
            ...user,
            uid: user.uid,
            userName: user.userName,
            qrToken,
          });
        }

        delete failedUsers.value[user.key];
        user.status = 'success';
      } else if ([201, 204].includes(userUpsertResponse.status)) {
        const response = userUpsertResponse.data;
        if (user.uid && user.userName) {
          // we updated the user if uid existed
          createdUsers.value.push({
            ...user,
            uid: user.uid,
            userName: user.userName,
            qrToken,
          });
        } else if (response && 'uid' in response && 'userName' in response) {
          createdUsers.value.push({
            ...user,
            qrToken,
            userName: response.userName,
            uid: response.uid,
          });
        } else {
          // Should never reach here. Maybe create user response type changed?
          console.error('user neither created nor updated');
        }

        delete failedUsers.value[user.key];
        user.status = 'success';
      } else {
        failedUsers.value[user.key] = user;
        user.status = 'error';
        user.errorMessages = toErrorMessage(userUpsertResponse);
      }
    } catch (error) {
      failedUsers.value[user.key] = user;
      user.status = 'error';
      user.errorMessages = toErrorMessage(error);
    }
  };

  const usersWithError = computed(() => {
    return Object.values(failedUsers.value);
  });

  return {
    isCreating,
    isUsersCreated,
    progress,
    createdUsers,
    usersWithError,
    createUsers,
    createUser,
  };
}
